import React, { useCallback, useContext, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { updateSubmissionDocuments } from 'apis/legitimizer/kyb/api';
import { KybSubmissionDocument, DocumentType, KybSubmissionData } from 'apis/legitimizer/types';

import DocumentFormFields from './DocumentFormFields';
import { SectionForm } from './SectionForm';
import { getDocumentsSchema } from './validation';
import { KybProcessContext } from '../KybProcessContext';
import { SectionConfigurationContext } from '../SectionConfigurationContext';
import { transformKybSubmissionDocumentsToDocumentsFormValues } from '../transformers';
import { DocumentsFormValues } from '../types';
import { useDocumentsFieldsConfigMemo } from '../useFieldsConfigMemo';

export type DocumentsProps = {
  feedback?: string;
  submissionDocuments: KybSubmissionDocument[];
  submissionData: KybSubmissionData;
  onSubmitSuccess: (section: string, skipSection?: boolean) => Promise<void>;
};

const resolveDocuments = (documents: KybSubmissionDocument[], type: DocumentType) =>
  documents?.filter(document => document.documentType === type) || [];

const Documents = ({ feedback, submissionDocuments, submissionData, onSubmitSuccess }: DocumentsProps) => {
  const intl = useIntl();
  const t = useCallback((id: string) => intl.formatMessage({ id }), [intl]);

  const sectionConfiguration = useContext(SectionConfigurationContext);
  const { processId, countryCode } = useContext(KybProcessContext);

  const [tradeRegistryDocumentIdList, setTradeRegistryDocumentIdList] = useState<number[]>([]);

  const documentsFieldsConfig = useDocumentsFieldsConfigMemo({ sectionConfiguration });

  const tradeRegistryDocuments = useMemo(() => {
    return resolveDocuments(submissionDocuments, 'REGISTRATION_CERTIFICATE');
  }, [submissionDocuments]);

  const hasExistingDocument = !!tradeRegistryDocuments.length;

  const isSoleTrader = submissionData.basicCompanyInfo?.businessDetails?.legalForm?.value === 'SOLE_TRADER';
  const isDocumentOptionalForSoleTrader = isSoleTrader && countryCode !== 'ISR';

  const documentsFormSchema = getDocumentsSchema({
    enforceValidation: !isDocumentOptionalForSoleTrader && hasExistingDocument,
    fieldsConfig: documentsFieldsConfig,
  });

  const initialFormValues = useMemo(() => {
    setTradeRegistryDocumentIdList(tradeRegistryDocuments.map(document => document.id));

    return transformKybSubmissionDocumentsToDocumentsFormValues(submissionDocuments);
  }, [submissionDocuments, tradeRegistryDocuments]);

  const handleSubmit = async (data: DocumentsFormValues) => {
    const newDocuments = data.registryExtractDocuments
      .map(documentWrapper => documentWrapper.file)
      // Array.filter doesn't narrow down the type but flatmap does.
      // https://github.com/microsoft/TypeScript/issues/16069#issuecomment-1234119963
      .flatMap(file => (!!file ? file : []));

    await updateSubmissionDocuments(processId, 'REGISTRATION_CERTIFICATE', {
      existingIds: tradeRegistryDocumentIdList,
      newDocuments,
    });

    await onSubmitSuccess(data.nextSection ? data.nextSection : 'documents', !!data.nextSection);
  };

  const resolveFormSubtitle = () => {
    if (isDocumentOptionalForSoleTrader) {
      return t('compliance.kyb.sections.documents.subtitle-sole-traders');
    }

    return t('compliance.kyb.sections.documents.subtitle');
  };

  return (
    <SectionForm
      subtitle={resolveFormSubtitle()}
      initialFormValues={initialFormValues}
      schema={documentsFormSchema}
      feedback={feedback}
      onSubmit={handleSubmit}
    >
      {() => (
        <DocumentFormFields
          config={{
            documents: documentsFieldsConfig,
          }}
          existingDocuments={tradeRegistryDocuments.filter(document =>
            tradeRegistryDocumentIdList.includes(document.id),
          )}
          isDocumentOptionalForSoleTrader={isDocumentOptionalForSoleTrader}
          setDocumentIdList={setTradeRegistryDocumentIdList}
        />
      )}
    </SectionForm>
  );
};

export default Documents;
